import type { ChatAttachment, ChatMessage, ChatModelOption, ChatProcessStep, ChatQuota, ChatSession, ChatSource } from "@/types";
import { api } from "@/api";
import { loadChatSidebarFirst } from "@/chatBootstrap";
import { ChatMessageCache } from "@/chatMessageCache";
import { isNearScrollBottom } from "@/chatScroll";
import { extractStreamingOutputText, extractStreamingReasoningSummaryText } from "@/chatStreamingOutput";
import { state } from "@/state";
import { escapeHtml, formatTime } from "@/utils";
import { toast } from "@/views/admin";

const CHAT_CAPABILITIES_STORAGE_KEY = "seektoken.chat.capabilities";
const CHAT_MODEL_STORAGE_KEY = "seektoken.chat.model";
const MAX_CHAT_ATTACHMENT_COUNT = 4;
const MAX_CHAT_ATTACHMENT_BYTES = 4 * 1024 * 1024;
const MAX_CHAT_ATTACHMENT_TOTAL_BYTES = 8 * 1024 * 1024;
const SUPPORTED_ATTACHMENT_TYPES = new Set([
  "image/jpeg", "image/png", "image/gif", "image/webp", "application/pdf", "text/plain", "text/markdown",
  "text/csv", "text/html", "text/xml", "application/json", "application/xml", "application/msword",
  "application/vnd.ms-excel", "application/vnd.ms-powerpoint",
  "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
  "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
  "application/vnd.openxmlformats-officedocument.presentationml.presentation"
]);

interface ChatCapabilities {
  webSearch: boolean;
  deepThinking: boolean;
}

interface ActiveChatRequest {
  controller: AbortController;
  cancelled: boolean;
}

let activeChatRequest: ActiveChatRequest | null = null;
let chatSessionCreation: Promise<void> | null = null;
let chatHistoryLoadVersion = 0;
let chatHistoryLoading = false;
const chatMessageCache = new ChatMessageCache();

/** Bind the scroll controls once; the messages element itself is re-rendered. */
export function initializeChatScrollFollow(): void {
  const messagesEl = document.querySelector<HTMLElement>("#chatMessages");
  const latestButton = document.querySelector<HTMLButtonElement>("#chatScrollToLatest");
  if (!messagesEl || !latestButton) return;

  messagesEl.addEventListener("scroll", updateChatScrollToLatestButton, { passive: true });
  latestButton.addEventListener("click", () => {
    scrollChatToBottom();
    updateChatScrollToLatestButton();
  });
  updateChatScrollToLatestButton();
}

// ===== 数据加载 =====

/** 加载聊天数据:会话列表 + 配额,若无会话自动创建一个 */
export async function loadChatData(): Promise<void> {
  try {
    await loadChatSidebarFirst({
      loadSessions: () => api<{ sessions: ChatSession[] }>("/api/chat/sessions"),
      loadQuota: () => api<ChatQuota>("/api/chat/quota"),
      loadModels: () => api<{ models: ChatModelOption[]; defaultModel: string }>("/api/chat/models"),
      onSessions: (sessionsData) => {
        state.chatSessions = sessionsData.sessions || [];
        chatMessageCache.retain(new Set(state.chatSessions.map((session) => session.id)));
        state.chatCurrentSessionId = "";
        state.chatMessages = [];
        chatHistoryLoading = false;
        // Render this independently useful surface immediately. The selected
        // transcript is loaded only after the user selects a conversation.
        renderChatPage();
        if (state.chatSessions.length === 0) {
          void createChatSession();
        }
      },
      onMetadata: ({ quota, models }) => {
        if (quota) state.chatQuota = quota;
        if (models) {
          state.chatAvailableModels = models.models || [];
          state.chatSelectedModel = chooseChatModel(
            state.chatAvailableModels,
            models.defaultModel || state.chatSelectedModel
          );
        }
        renderChatSidebar();
        renderChatHeader();
        renderChatInput();
      }
    });
  } catch (error) {
    console.error("loadChatData failed:", error);
  }
}

/** 刷新配额 */
async function refreshQuota(): Promise<void> {
  try {
    state.chatQuota = await api<ChatQuota>("/api/chat/quota");
  } catch {
    /* ignore */
  }
}

/** 选中某个会话,加载其消息 */
export async function selectChatSession(sessionId: string): Promise<void> {
  if (state.chatStreaming) return;
  const loadVersion = ++chatHistoryLoadVersion;
  state.chatCurrentSessionId = sessionId;
  state.chatStreamingText = "";
  state.chatStreamingSteps = [];
  state.chatStreamingSources = [];
  state.chatPendingAttachments = [];
  const cachedMessages = chatMessageCache.get(sessionId);
  if (cachedMessages) {
    chatHistoryLoading = false;
    state.chatMessages = cachedMessages;
    renderChatPage();
    return;
  }
  chatHistoryLoading = true;
  state.chatMessages = [];
  renderChatPage();
  try {
    const data = await api<{ messages: ChatMessage[] }>(
      `/api/chat/sessions/${encodeURIComponent(sessionId)}/messages`
    );
    if (loadVersion !== chatHistoryLoadVersion) return;
    state.chatMessages = data.messages || [];
    chatMessageCache.set(sessionId, state.chatMessages);
  } catch (error) {
    if (loadVersion !== chatHistoryLoadVersion) return;
    console.error("selectChatSession failed:", error);
    state.chatMessages = [];
  }
  if (loadVersion !== chatHistoryLoadVersion) return;
  chatHistoryLoading = false;
  renderChatPage();
}

/** 新建会话 */
export async function createChatSession(): Promise<void> {
  if (chatSessionCreation) return chatSessionCreation;
  chatSessionCreation = (async () => {
    try {
      const data = await api<{ session: ChatSession }>("/api/chat/sessions", {
        method: "POST",
        body: JSON.stringify({})
      });
      state.chatSessions = [data.session, ...state.chatSessions];
      chatMessageCache.set(data.session.id, []);
      await selectChatSession(data.session.id);
    } catch (error) {
      console.error("createChatSession failed:", error);
    }
  })().finally(() => {
    chatSessionCreation = null;
  });
  return chatSessionCreation;
}

/** 删除会话 */
export async function deleteChatSession(sessionId: string): Promise<void> {
  if (state.chatStreaming && state.chatCurrentSessionId === sessionId) {
    toast("回复生成中，暂时不能删除会话。", "error");
    return;
  }
  try {
    await api(`/api/chat/sessions/${encodeURIComponent(sessionId)}`, { method: "DELETE" });
    chatMessageCache.delete(sessionId);
    state.chatSessions = state.chatSessions.filter((s) => s.id !== sessionId);
    if (state.chatCurrentSessionId === sessionId) {
      if (state.chatSessions.length > 0) {
        await selectChatSession(state.chatSessions[0].id);
      } else {
        state.chatCurrentSessionId = "";
        state.chatMessages = [];
        await createChatSession();
      }
    }
    renderChatPage();
    toast("会话已删除。", "success");
  } catch (error) {
    console.error("deleteChatSession failed:", error);
    toast((error as Error).message || "删除会话失败，请稍后重试。", "error");
  }
}

// ===== 发送消息(SSE 流式) =====

/** 取消当前正在生成的回复，关闭客户端连接以终止服务端和上游请求。 */
export function cancelChatMessage(): void {
  if (!state.chatStreaming || !activeChatRequest || activeChatRequest.cancelled) return;

  activeChatRequest.cancelled = true;
  state.chatStreamingStatus = "正在取消回复";
  state.chatStreamingSteps = state.chatStreamingSteps.map((step) =>
    step.status === "active" ? { ...step, label: "正在取消", status: "done" } : step
  );
  renderChatMessages();
  activeChatRequest.controller.abort();
}

/** 发送消息:写入 user 消息,POST /api/chat/sessions/:id/messages,SSE 流式接收 */
export async function sendChatMessage(): Promise<void> {
  if (state.chatStreaming) return;

  const inputEl = document.querySelector<HTMLTextAreaElement>("#chatInput");
  const message = (inputEl?.value || "").trim();
  const attachments = state.chatPendingAttachments;
  if (!message && attachments.length === 0) return;

  // The composer remains available while the history list has no selected
  // session. Sending from that state must start the conversation instead of
  // silently returning with no request.
  if (!state.chatCurrentSessionId) {
    await createChatSession();
  }
  const sessionId = state.chatCurrentSessionId;
  if (!sessionId) {
    toast("新建对话失败，请刷新页面后重试。", "error");
    return;
  }

  const capabilities = readChatCapabilities();
  const modelName = state.chatSelectedModel || chooseChatModel(state.chatAvailableModels, "");
  saveChatCapabilities(capabilities);
  saveChatModel(modelName);
  if (inputEl) inputEl.value = "";

  // 先在前端插入 user 消息(乐观更新,后端会持久化)
  const userMsg: ChatMessage = {
    id: `temp_${Date.now()}`,
    sessionId,
    role: "user",
    content: message || "请阅读并分析所附内容。",
    attachments,
    createdAt: new Date().toISOString(),
    usage: null
  };
  state.chatMessages = [...state.chatMessages, userMsg];
  cacheCurrentChatMessages();
  touchCurrentChatSession({ role: "user", content: message || attachments.map((attachment) => attachment.name).join("、"), createdAt: userMsg.createdAt });
  state.chatPendingAttachments = [];
  renderChatPendingAttachments();

  // 进入流式状态
  state.chatStreaming = true;
  state.chatStreamingText = "";
  state.chatStreamingReasoning = "";
  state.chatStreamingStatus = capabilities.deepThinking ? `模型 ${modelName} 深度思考中` : `正在连接 ${modelName}`;
  state.chatStreamingSteps = [{ key: "connect", label: "正在连接模型", status: "active" }];
  if (capabilities.deepThinking) {
    state.chatStreamingSteps.push({ key: "reasoning", label: "深度思考已开启", status: "active" });
  }
  if (capabilities.webSearch) {
    state.chatStreamingSteps.push({ key: "search", label: "联网搜索已开启", status: "active" });
  }
  state.chatStreamingSources = [];
  renderChatMessages();
  scrollChatToBottom();

  const request: ActiveChatRequest = {
    controller: new AbortController(),
    cancelled: false
  };
  activeChatRequest = request;

  try {
    const response = await fetch(
      `/api/chat/sessions/${encodeURIComponent(sessionId)}/messages`,
      {
        method: "POST",
        credentials: "include",
        headers: {
          "content-type": "application/json",
          "x-csrf-token": state.csrfToken
        },
        body: JSON.stringify({ message, attachments, capabilities, model: modelName }),
        signal: request.controller.signal
      }
    );

    if (!response.ok) {
      const errText = await response.text().catch(() => "");
      let errMsg = `请求失败(${response.status})`;
      try {
        const errJson = JSON.parse(errText);
        errMsg = errJson?.error?.message || errMsg;
      } catch {
        /* ignore */
      }
      // 错误也作为 assistant 消息显示
      state.chatMessages = [
        ...state.chatMessages,
        {
          id: `err_${Date.now()}`,
          sessionId,
          role: "assistant",
          content: `⚠️ ${errMsg}`,
          createdAt: new Date().toISOString(),
          usage: null
        }
      ];
      state.chatStreaming = false;
      state.chatStreamingText = "";
      renderChatMessages();
      return;
    }

    // SSE 流式读取
    const reader = response.body?.getReader();
    if (!reader) throw new Error("无法读取响应流");

    const decoder = new TextDecoder();
    let buffer = "";

    while (true) {
      const { done, value } = await reader.read();
      if (done) break;
      buffer += decoder.decode(value, { stream: true });

      // 按 SSE 帧分隔(\n\n)
      let sep;
      while ((sep = buffer.indexOf("\n\n")) !== -1) {
        const frame = buffer.slice(0, sep);
        buffer = buffer.slice(sep + 2);
        const parsed = parseSseFrame(frame);
        if (!parsed) continue;
        if (parsed.type === "chat.error") {
          const error = new Error(streamErrorMessage(parsed));
          Object.assign(error, { chatStreamError: true });
          throw error;
        }
        updateStreamingState(parsed);
        const reasoningText = extractStreamingReasoningSummaryText(parsed);
        if (reasoningText) {
          state.chatStreamingReasoning += reasoningText;
          renderChatStreamingReasoning();
        }
        const outputText = extractStreamingOutputText(parsed);
        if (outputText && (parsed.type === "response.output_text.delta" || !state.chatStreamingText)) {
          const followLiveReply = shouldFollowChatTail();
          state.chatStreamingText += outputText;
          renderChatStreamingText();
          if (followLiveReply) scrollChatToBottom();
          updateChatScrollToLatestButton();
        }
      }
    }

    // 流结束:把累积的文本作为 assistant 消息加入
    if (state.chatStreamingText) {
      const assistantCreatedAt = new Date().toISOString();
      state.chatMessages = [
        ...state.chatMessages,
        {
          id: `ai_${Date.now()}`,
          sessionId,
          role: "assistant",
          content: state.chatStreamingText,
          sources: state.chatStreamingSources,
          createdAt: assistantCreatedAt,
          usage: null
        }
      ];
      touchCurrentChatSession({
        role: "assistant",
        content: state.chatStreamingText,
        createdAt: assistantCreatedAt
      });
    }
  } catch (error) {
    // 用户取消会关闭 SSE 连接；服务端据此中止上游请求并记录为 cancelled。
    if (request.cancelled) {
      toast("已取消本次回复。", "success");
    } else if (state.chatStreamingText) {
      // 网络错误或流中断
      const assistantCreatedAt = new Date().toISOString();
      state.chatMessages = [
        ...state.chatMessages,
        {
          id: `ai_${Date.now()}`,
          sessionId,
          role: "assistant",
          content: state.chatStreamingText + "\n\n⚠️ (流中断)",
          sources: state.chatStreamingSources,
          createdAt: assistantCreatedAt,
          usage: null
        }
      ];
      touchCurrentChatSession({
        role: "assistant",
        content: state.chatStreamingText,
        createdAt: assistantCreatedAt
      });
    } else {
      const streamError = error as Error & { chatStreamError?: boolean };
      state.chatMessages = [
        ...state.chatMessages,
        {
          id: `err_${Date.now()}`,
          sessionId,
          role: "assistant",
          content: `⚠️ ${streamError.chatStreamError ? streamError.message : `网络错误: ${streamError.message}`}`,
          createdAt: new Date().toISOString(),
          usage: null
        }
      ];
    }
  } finally {
    const followLiveReply = shouldFollowChatTail();
    if (activeChatRequest === request) activeChatRequest = null;
    state.chatStreaming = false;
    state.chatStreamingText = "";
    state.chatStreamingReasoning = "";
    state.chatStreamingStatus = "";
    state.chatStreamingSteps = [];
    state.chatStreamingSources = [];
    cacheCurrentChatMessages();
    renderChatMessages();
    if (followLiveReply) scrollChatToBottom();
    updateChatScrollToLatestButton();
    // 刷新配额
    await refreshQuota();
    renderChatSidebar();
  }
}

function buildChatTitle(content: string): string {
  return content.replace(/\s+/g, " ").trim().slice(0, 60) || "新对话";
}

function buildChatSnippet(content: string): string {
  return content.replace(/\s+/g, " ").trim().slice(0, 96);
}

function touchCurrentChatSession(message: {
  role: "user" | "assistant";
  content: string;
  createdAt: string;
}): void {
  const sessionId = state.chatCurrentSessionId;
  if (!sessionId) return;
  state.chatSessions = state.chatSessions.map((session) => {
    if (session.id !== sessionId) return session;
    const hasRealTitle = session.title && session.title !== "新对话";
    return {
      ...session,
      title: hasRealTitle ? session.title : buildChatTitle(message.content),
      lastMessageRole: message.role,
      lastMessageContent: message.content,
      lastMessageAt: message.createdAt,
      messageCount: (session.messageCount || 0) + 1,
      updatedAt: message.createdAt
    };
  });
  renderChatHeader();
}

export function initializeChatCapabilityControls(): void {
  const capabilities = loadChatCapabilities();
  setChatCapabilityControls(capabilities);
  document.querySelectorAll<HTMLInputElement>("[data-chat-capability]").forEach((input) => {
    input.addEventListener("change", () => {
      saveChatCapabilities(readChatCapabilities());
    });
  });
}

export function initializeChatModelControls(): void {
  const select = document.querySelector<HTMLSelectElement>("#chatModelSelect");
  if (!select) return;
  select.addEventListener("change", () => {
    const requestedModel = String(select.value || "").trim();
    const available = new Set(
      state.chatAvailableModels
        .map((model) => String(model.modelName || "").trim())
        .filter(Boolean)
    );
    state.chatSelectedModel = available.has(requestedModel)
      ? requestedModel
      : chooseChatModel(state.chatAvailableModels, requestedModel);
    saveChatModel(state.chatSelectedModel);
    renderChatPage();
  });
}

function readChatCapabilities(): ChatCapabilities {
  const webSearchEl = document.querySelector<HTMLInputElement>("#chatWebSearchToggle");
  const deepThinkingEl = document.querySelector<HTMLInputElement>("#chatDeepThinkingToggle");
  return {
    webSearch: Boolean(webSearchEl?.checked),
    deepThinking: Boolean(deepThinkingEl?.checked)
  };
}

function setChatCapabilityControls(capabilities: ChatCapabilities): void {
  const webSearchEl = document.querySelector<HTMLInputElement>("#chatWebSearchToggle");
  const deepThinkingEl = document.querySelector<HTMLInputElement>("#chatDeepThinkingToggle");
  if (webSearchEl) webSearchEl.checked = capabilities.webSearch;
  if (deepThinkingEl) deepThinkingEl.checked = capabilities.deepThinking;
}

function loadChatCapabilities(): ChatCapabilities {
  try {
    const raw = localStorage.getItem(CHAT_CAPABILITIES_STORAGE_KEY);
    const parsed = raw ? JSON.parse(raw) : {};
    return {
      webSearch: parsed?.webSearch === true,
      deepThinking: parsed?.deepThinking === true
    };
  } catch {
    return { webSearch: false, deepThinking: false };
  }
}

function saveChatCapabilities(capabilities: ChatCapabilities): void {
  try {
    localStorage.setItem(CHAT_CAPABILITIES_STORAGE_KEY, JSON.stringify(capabilities));
  } catch {
    /* localStorage is only a convenience. */
  }
}

function loadStoredChatModel(): string {
  try {
    return String(localStorage.getItem(CHAT_MODEL_STORAGE_KEY) || "").trim();
  } catch {
    return "";
  }
}

function saveChatModel(modelName: string): void {
  try {
    localStorage.setItem(CHAT_MODEL_STORAGE_KEY, modelName);
  } catch {
    /* localStorage is only a convenience. */
  }
}

function chooseChatModel(models: ChatModelOption[], fallbackModel: string): string {
  const available = models.map((model) => model.modelName).filter(Boolean);
  const preferred = loadStoredChatModel();
  if (preferred && available.includes(preferred)) return preferred;
  if (fallbackModel && available.includes(fallbackModel)) return fallbackModel;
  const defaultModel = models.find((model) => model.isDefault)?.modelName || "";
  if (defaultModel && available.includes(defaultModel)) return defaultModel;
  return available[0] || fallbackModel || "";
}

/** 解析 SSE 帧,提取 data JSON */
function parseSseFrame(frame: string): { type?: string; delta?: string; [k: string]: unknown } | null {
  for (const line of frame.split("\n")) {
    const trimmed = line.trimStart();
    if (trimmed.startsWith("data:")) {
      const jsonStr = trimmed.slice(5).trim();
      if (!jsonStr || jsonStr === "[DONE]") return null;
      try {
        return JSON.parse(jsonStr);
      } catch {
        return null;
      }
    }
  }
  return null;
}

function streamErrorMessage(event: Record<string, unknown>): string {
  const error = isRecord(event.error) ? event.error : null;
  return typeof error?.message === "string" && error.message.trim()
    ? error.message
    : "模型服务暂不可用，请稍后重试";
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return Boolean(value && typeof value === "object");
}

function sourceTitleFromUrl(url: string): string {
  try {
    const parsed = new URL(url);
    return parsed.hostname.replace(/^www\./, "");
  } catch {
    return url;
  }
}

function normalizeSource(value: unknown): ChatSource | null {
  if (!isRecord(value)) return null;
  const url = typeof value.url === "string" ? value.url.trim() : "";
  if (!/^https?:\/\//i.test(url)) return null;
  const title = typeof value.title === "string" && value.title.trim()
    ? value.title.trim()
    : sourceTitleFromUrl(url);
  return { title, url };
}

function extractSources(event: Record<string, unknown>): ChatSource[] {
  const sources: ChatSource[] = [];
  const item = isRecord(event.item) ? event.item : null;
  const action = item && isRecord(item.action) ? item.action : null;
  if (Array.isArray(action?.sources)) {
    sources.push(...action.sources.map(normalizeSource).filter((s): s is ChatSource => Boolean(s)));
  }

  const part = isRecord(event.part) ? event.part : null;
  if (Array.isArray(part?.annotations)) {
    sources.push(...part.annotations.map(normalizeSource).filter((s): s is ChatSource => Boolean(s)));
  }
  return sources;
}

function addStreamingSources(sources: ChatSource[]): void {
  if (sources.length === 0) return;
  const existing = new Set(state.chatStreamingSources.map((source) => source.url));
  const next = [...state.chatStreamingSources];
  for (const source of sources) {
    if (existing.has(source.url)) continue;
    existing.add(source.url);
    next.push(source);
  }
  state.chatStreamingSources = next.slice(0, 8);
  renderChatStreamingSources();
}

function upsertStreamingStep(step: ChatProcessStep): void {
  const current = state.chatStreamingSteps || [];
  const existing = current.find((item) => item.key === step.key);
  if (existing) {
    state.chatStreamingSteps = current.map((item) => item.key === step.key ? { ...item, ...step } : item);
  } else {
    state.chatStreamingSteps = [...current, step].slice(-8);
  }
  renderChatStreamingSteps();
}

function updateStreamingState(event: { type?: string; [k: string]: unknown }): void {
  const type = String(event.type || "");
  if (!type) return;

  addStreamingSources(extractSources(event));

  if (type === "chat.progress") {
    updateChatProgress(event);
  } else if (type === "response.created" || type === "response.in_progress") {
    state.chatStreamingStatus = "已连接模型";
    upsertStreamingStep({ key: "connect", label: "已连接模型", status: "done" });
  } else if (type === "response.output_item.added" && isRecord(event.item) && event.item.type === "web_search_call") {
    state.chatStreamingStatus = "已开始联网搜索";
    upsertStreamingStep({ key: "search", label: "已开始联网搜索", status: "active" });
  } else if (type === "response.web_search_call.in_progress" || type === "response.web_search_call.searching") {
    state.chatStreamingStatus = "联网搜索中";
    upsertStreamingStep({ key: "search", label: "联网搜索中", status: "active" });
  } else if (type === "response.web_search_call.completed") {
    state.chatStreamingStatus = "搜索完成";
    upsertStreamingStep({ key: "search", label: "搜索完成", status: "done" });
  } else if (type.includes("reasoning")) {
    state.chatStreamingStatus = "深度思考中";
    upsertStreamingStep({ key: "reasoning", label: "深度思考中", status: "active" });
  } else if (type === "response.output_item.added" && isRecord(event.item) && event.item.type === "message") {
    state.chatStreamingStatus = "正在组织答案";
    upsertStreamingStep({ key: "compose", label: "正在组织答案", status: "active" });
  } else if (type === "response.content_part.added") {
    state.chatStreamingStatus = "正在组织答案";
    upsertStreamingStep({ key: "compose", label: "正在组织答案", status: "active" });
  } else if (type === "response.output_text.delta") {
    state.chatStreamingStatus = "生成回答中";
    upsertStreamingStep({ key: "compose", label: "正在生成回答", status: "active" });
  } else if (type === "response.output_text.done") {
    upsertStreamingStep({ key: "compose", label: "回答生成完成", status: "done" });
  } else if (type === "response.completed") {
    state.chatStreamingStatus = "回答完成";
    upsertStreamingStep({ key: "compose", label: "回答完成", status: "done" });
  }
  renderChatStreamingStatus();
}

function updateChatProgress(event: Record<string, unknown>): void {
  const stage = String(event.stage || "");
  const timing = isRecord(event.timing) ? event.timing : {};
  const localPreparationMs = readTimingMs(timing.localPreparationMs);
  const upstreamHeadersMs = readTimingMs(timing.upstreamHeadersMs);
  const firstChunkMs = readTimingMs(timing.firstChunkMs);
  const firstOutputTextMs = readTimingMs(timing.firstOutputTextMs);

  if (stage === "request_prepared") {
    state.chatStreamingStatus = "请求已受理，正在连接上游模型";
    upsertStreamingStep({ key: "connect", label: `本地准备 ${formatTimingMs(localPreparationMs)}，等待上游首包`, status: "active" });
  } else if (stage === "upstream_headers") {
    state.chatStreamingStatus = "上游已响应，等待首个事件";
    upsertStreamingStep({
      key: "connect",
      label: `上游建连 ${formatTimingMs(upstreamHeadersMs)}（本地 ${formatTimingMs(localPreparationMs)}）`,
      status: "done"
    });
    upsertStreamingStep({ key: "first-event", label: "等待首个上游事件", status: "active" });
  } else if (stage === "first_chunk") {
    state.chatStreamingStatus = "已收到上游首个事件";
    upsertStreamingStep({ key: "first-event", label: `首个上游事件 ${formatTimingMs(firstChunkMs)}`, status: "done" });
  } else if (stage === "first_output_text") {
    state.chatStreamingStatus = "生成回答中";
    upsertStreamingStep({ key: "compose", label: `首个回答文本 ${formatTimingMs(firstOutputTextMs)}`, status: "active" });
  }
}

function readTimingMs(value: unknown): number | null {
  const milliseconds = Number(value);
  return Number.isFinite(milliseconds) && milliseconds >= 0 ? milliseconds : null;
}

function formatTimingMs(milliseconds: number | null): string {
  if (milliseconds === null) return "—";
  return milliseconds >= 1000 ? `${(milliseconds / 1000).toFixed(1)} 秒` : `${milliseconds} ms`;
}

function renderInlineMarkdown(value: string): string {
  const codeSpans: string[] = [];
  let html = escapeHtml(value).replace(/`([^`\n]+)`/g, (_match, code) => {
    const index = codeSpans.push(`<code>${code}</code>`) - 1;
    return `@@CODE${index}@@`;
  });

  html = html.replace(/\[([^\]]+)\]\((https?:\/\/[^)\s]+)\)/g, (_match, label, url) => {
    const safeUrl = String(url).replaceAll("&amp;", "&");
    return `<a href="${escapeHtml(safeUrl)}" target="_blank" rel="noreferrer">${label}</a>`;
  });
  html = html
    .replace(/\*\*([^*]+)\*\*/g, "<strong>$1</strong>")
    .replace(/__([^_]+)__/g, "<strong>$1</strong>");

  for (let index = 0; index < codeSpans.length; index += 1) {
    html = html.replace(`@@CODE${index}@@`, codeSpans[index]);
  }
  return html;
}

function renderMarkdown(content: string): string {
  const lines = String(content || "").replace(/\r\n/g, "\n").split("\n");
  const blocks: string[] = [];
  let paragraph: string[] = [];
  let listItems: string[] = [];
  let orderedListItems: string[] = [];
  let inCode = false;
  let codeLines: string[] = [];

  const flushParagraph = () => {
    if (paragraph.length === 0) return;
    blocks.push(`<p>${paragraph.map(renderInlineMarkdown).join("<br>")}</p>`);
    paragraph = [];
  };
  const flushList = () => {
    if (listItems.length) {
      blocks.push(`<ul>${listItems.map((item) => `<li>${renderInlineMarkdown(item)}</li>`).join("")}</ul>`);
      listItems = [];
    }
    if (orderedListItems.length) {
      blocks.push(`<ol>${orderedListItems.map((item) => `<li>${renderInlineMarkdown(item)}</li>`).join("")}</ol>`);
      orderedListItems = [];
    }
  };

  const renderTable = (rows: string[][]): string => {
    if (rows.length === 0) return "";
    const [header, ...bodyRows] = rows;
    return `
      <table>
        <thead><tr>${header.map((cell) => `<th>${renderInlineMarkdown(cell.trim())}</th>`).join("")}</tr></thead>
        <tbody>${bodyRows.map((row) => `<tr>${row.map((cell) => `<td>${renderInlineMarkdown(cell.trim())}</td>`).join("")}</tr>`).join("")}</tbody>
      </table>
    `;
  };
  const splitTableRow = (row: string): string[] =>
    row.trim().replace(/^\|/, "").replace(/\|$/, "").split("|");
  const isTableSeparator = (row: string): boolean =>
    /^\|?\s*:?-{3,}:?\s*(\|\s*:?-{3,}:?\s*)+\|?$/.test(row.trim());

  for (let index = 0; index < lines.length; index += 1) {
    const line = lines[index];
    const trimmed = line.trim();
    const fence = trimmed.match(/^```(\w+)?/);
    if (fence) {
      flushParagraph();
      flushList();
      if (inCode) {
        blocks.push(`<pre><code>${escapeHtml(codeLines.join("\n"))}</code></pre>`);
        inCode = false;
        codeLines = [];
      } else {
        inCode = true;
      }
      continue;
    }
    if (inCode) {
      codeLines.push(line);
      continue;
    }
    if (!trimmed) {
      flushParagraph();
      flushList();
      continue;
    }
    if (/^\|.+\|$/.test(trimmed) && isTableSeparator(lines[index + 1] || "")) {
      flushParagraph();
      flushList();
      const rows = [splitTableRow(trimmed)];
      index += 2;
      while (index < lines.length && /^\|.+\|$/.test(lines[index].trim())) {
        rows.push(splitTableRow(lines[index]));
        index += 1;
      }
      index -= 1;
      blocks.push(renderTable(rows));
      continue;
    }
    const heading = trimmed.match(/^(#{1,4})\s+(.+)$/);
    if (heading) {
      flushParagraph();
      flushList();
      const level = Math.min(heading[1].length + 2, 5);
      blocks.push(`<h${level}>${renderInlineMarkdown(heading[2])}</h${level}>`);
      continue;
    }
    const unordered = trimmed.match(/^[-*]\s+(.+)$/);
    if (unordered) {
      flushParagraph();
      orderedListItems = [];
      listItems.push(unordered[1]);
      continue;
    }
    const ordered = trimmed.match(/^\d+\.\s+(.+)$/);
    if (ordered) {
      flushParagraph();
      listItems = [];
      orderedListItems.push(ordered[1]);
      continue;
    }
    const quote = trimmed.match(/^>\s?(.+)$/);
    if (quote) {
      flushParagraph();
      flushList();
      blocks.push(`<blockquote>${renderInlineMarkdown(quote[1])}</blockquote>`);
      continue;
    }
    paragraph.push(trimmed);
  }

  if (inCode) {
    blocks.push(`<pre><code>${escapeHtml(codeLines.join("\n"))}</code></pre>`);
  }
  flushParagraph();
  flushList();
  return blocks.join("");
}

// ===== 渲染 =====

/** 渲染聊天页面(侧边栏 + 消息区 + 输入框) */
export function renderChatPage(): void {
  const layout = document.querySelector<HTMLElement>("#layout-chat");
  if (!layout) return;

  renderChatSidebar();
  renderChatHeader();
  renderChatMessages();
  renderChatInput();
}

function renderChatHeader(): void {
  const activeSession = state.chatSessions.find((session) => session.id === state.chatCurrentSessionId);
  const title = activeSession?.title && activeSession.title !== "新对话"
    ? activeSession.title
    : buildChatTitle(activeSession?.lastMessageContent || "");
  const titleEl = document.querySelector<HTMLElement>("#chatActiveTitle");
  const metaEl = document.querySelector<HTMLElement>("#chatActiveMeta");
  const modelEl = document.querySelector<HTMLElement>("#chatModelIndicator");
  if (titleEl) titleEl.textContent = title || "新对话";
  if (metaEl) {
    metaEl.textContent = state.chatStreaming
      ? "正在生成回复…"
      : state.chatMessages.length
        ? `${state.chatMessages.length} 条消息 · 可联网搜索、深度思考`
        : "可联网搜索、深度思考与流式输出";
  }
  if (modelEl) modelEl.textContent = state.chatSelectedModel || "模型加载中";
}

/** 渲染左侧会话列表 */
function renderChatSidebar(): void {
  const sidebar = document.querySelector<HTMLElement>("#chatSidebar");
  if (!sidebar) return;
  const quota = state.chatQuota;
  const quotaText = quota
    ? `本月 ${quota.used} 轮`
    : "";
  const freeChatText = quota?.freeChat
    ? `赠送对话 ${quota.freeChat.remaining}/${quota.freeChat.total}`
    : "";
  const modelText = state.chatSelectedModel
    ? `当前模型 ${state.chatSelectedModel}`
    : "";

  sidebar.innerHTML = `
    <div class="chat-sidebar-header">
      <div class="chat-brand">
        <span class="chat-brand-mark">ST</span>
        <div>
          <p class="chat-sidebar-kicker">SeekToken</p>
          <h2>对话工作台</h2>
        </div>
      </div>
      <button class="primary-button full-button chat-new-session" data-action="chat-new-session">+ 开始新对话</button>
      <div class="chat-sidebar-status">
        ${quotaText ? `<span>${escapeHtml(quotaText)}</span>` : ""}
        ${freeChatText ? `<span>${escapeHtml(freeChatText)}</span>` : ""}
        ${modelText ? `<span>${escapeHtml(modelText)}</span>` : ""}
      </div>
    </div>
    <div class="chat-session-list">
      <p class="chat-session-list-label">最近对话</p>
      ${state.chatSessions.length === 0
        ? `<p class="muted chat-empty">还没有对话</p>`
        : state.chatSessions
            .map(
              (s) => {
                const title = s.title && s.title !== "新对话"
                  ? s.title
                  : buildChatTitle(s.lastMessageContent || "");
                const snippet = buildChatSnippet(s.lastMessageContent || "");
                const roleLabel = s.lastMessageRole === "assistant" ? "SeekToken" : "你";
                const updatedAt = s.lastMessageAt || s.updatedAt;
                return `
        <div class="chat-session-item ${s.id === state.chatCurrentSessionId ? "active" : ""}" data-action="chat-select-session" data-id="${escapeHtml(s.id)}">
          <div class="chat-session-title">${escapeHtml(title || "新对话")}</div>
          <div class="chat-session-snippet">${snippet ? `${escapeHtml(roleLabel)}: ${escapeHtml(snippet)}` : "还没有消息"}</div>
          <div class="chat-session-meta muted">
            <span>${escapeHtml(formatTime(updatedAt))}</span>
          </div>
          <button class="chat-session-delete ghost-button" data-action="chat-delete-session" data-id="${escapeHtml(s.id)}" title="${state.chatStreaming && state.chatCurrentSessionId === s.id ? "回复生成中，暂不可删除" : "删除"}" ${state.chatStreaming && state.chatCurrentSessionId === s.id ? "disabled aria-disabled=\"true\"" : ""}>×</button>
        </div>
      `;
              }
            )
            .join("")}
    </div>
  `;
}

/** 渲染消息区 */
function renderChatMessages(): void {
  const messagesEl = document.querySelector<HTMLElement>("#chatMessages");
  if (!messagesEl) return;

  const messages = state.chatMessages || [];
  const isEmpty = messages.length === 0 && !state.chatStreaming;

  messagesEl.innerHTML = chatHistoryLoading
    ? `<div class="chat-empty-state"><p class="muted">正在加载对话内容…</p></div>`
    : isEmpty && state.chatSessions.length > 0 && !state.chatCurrentSessionId
      ? `<div class="chat-empty-state"><h3>选择一段对话</h3><p class="muted">历史内容会在你打开该对话时加载。</p></div>`
      : isEmpty
      ? `<div class="chat-empty-state">
        <div class="chat-empty-icon">ST</div>
        <h3>今天想探索什么?</h3>
        <p class="muted">默认使用轻量聊天;需要实时资料或复杂推理时,可在输入框上方打开对应开关。</p>
        <div class="chat-suggestion-grid">
          ${[
            "总结一下今天 AI 行业的重要新闻,我会打开联网搜索",
            "深度分析一下这个产品接下来最该优化的功能,我会打开深度思考",
            "帮我写一份可执行的 PostgreSQL 上线检查清单",
            "对比 ChatGPT 和豆包的聊天体验差异"
          ].map((prompt) => `
            <button type="button" class="chat-suggestion" data-action="chat-suggest" data-prompt="${escapeHtml(prompt)}">
              ${escapeHtml(prompt)}
            </button>
          `).join("")}
        </div>
      </div>`
    : messages.map((m) => renderMessage(m)).join("") +
      (state.chatStreaming
        ? renderStreamingMessage(state.chatStreamingText)
        : "");

  // 输入框状态
  const inputEl = document.querySelector<HTMLTextAreaElement>("#chatInput");
  const sendBtn = document.querySelector<HTMLButtonElement>("#chatSendBtn");
  if (inputEl) inputEl.disabled = state.chatStreaming;
  if (sendBtn) sendBtn.disabled = state.chatStreaming;
  const cancelBtn = document.querySelector<HTMLButtonElement>("#chatCancelBtn");
  if (cancelBtn) {
    cancelBtn.hidden = !state.chatStreaming;
    cancelBtn.disabled = !state.chatStreaming || Boolean(activeChatRequest?.cancelled);
  }
  const attachmentButton = document.querySelector<HTMLButtonElement>("#chatAttachmentButton");
  if (attachmentButton) attachmentButton.disabled = state.chatStreaming;
  const attachmentInput = document.querySelector<HTMLInputElement>("#chatAttachmentInput");
  if (attachmentInput) attachmentInput.disabled = state.chatStreaming;
  document.querySelectorAll<HTMLInputElement>("[data-chat-capability]").forEach((input) => {
    input.disabled = state.chatStreaming;
  });
  const modelSelect = document.querySelector<HTMLSelectElement>("#chatModelSelect");
  if (modelSelect) {
    modelSelect.disabled = state.chatStreaming || state.chatAvailableModels.length <= 1;
  }
  updateChatScrollToLatestButton();
}

/** 渲染单条消息 */
function renderMessage(m: ChatMessage): string {
  const isUser = m.role === "user";
  return `
    <div class="chat-message ${isUser ? "chat-message-user" : "chat-message-assistant"}">
      ${isUser ? "" : `<div class="chat-message-avatar">ST</div>`}
      <div class="chat-message-body">
        ${isUser ? "" : `<div class="chat-message-label">SeekToken</div>`}
        <div class="chat-message-content">${isUser ? escapeHtml(m.content) : renderMarkdown(m.content)}</div>
        ${isUser ? renderChatAttachments(m.attachments || []) : ""}
        ${!isUser ? renderSources(m.sources || []) : ""}
        <div class="chat-message-meta muted">${isUser ? "你" : "SeekToken"} · ${escapeHtml(formatTime(m.createdAt))}</div>
      </div>
    </div>
  `;
}

/** 渲染流式输出中的消息 */
function renderStreamingMessage(text: string): string {
  return `
    <div class="chat-message chat-message-assistant chat-message-streaming">
      <div class="chat-message-avatar">ST</div>
      <div class="chat-message-body">
        <div class="chat-message-label">SeekToken</div>
        <div class="chat-message-content">
          <div class="chat-stream-status">${escapeHtml(state.chatStreamingStatus || "连接模型中")}<span></span></div>
          <div class="chat-stream-steps">${renderProcessSteps()}</div>
          ${renderStreamingReasoning(state.chatStreamingReasoning)}
          <div class="chat-stream-text">${renderMarkdown(text || "")}<span class="chat-cursor">▋</span></div>
          <div class="chat-stream-sources">${renderSources(state.chatStreamingSources)}</div>
        </div>
      </div>
    </div>
  `;
}

function renderStreamingReasoning(reasoning: string): string {
  if (!reasoning) return "";
  return `
    <details class="reasoning-block chat-stream-reasoning" open>
      <summary>思考摘要</summary>
      <div class="reasoning-content">${renderMarkdown(reasoning)}</div>
    </details>
  `;
}

function renderProcessSteps(): string {
  const steps = state.chatStreamingSteps || [];
  if (steps.length === 0) return "";
  return steps.map((step) => `
    <div class="chat-process-step ${step.status === "done" ? "done" : "active"}">
      <span></span>${escapeHtml(step.label)}
    </div>
  `).join("");
}

function renderSources(sources: ChatSource[]): string {
  if (!sources.length) return "";
  return `
    <div class="chat-sources">
      <div class="chat-sources-title">Sources</div>
      <div class="chat-source-list">
        ${sources.map((source, index) => `
          <a class="chat-source-link" href="${escapeHtml(source.url)}" target="_blank" rel="noreferrer">
            <span>${index + 1}</span>${escapeHtml(source.title || source.url)}
          </a>
        `).join("")}
      </div>
    </div>
  `;
}

function renderChatAttachments(attachments: ChatAttachment[]): string {
  if (!attachments.length) return "";
  return `<div class="chat-attachment-list">${attachments.map((attachment) => {
    const label = `${attachment.name} (${formatAttachmentSize(attachment.size)})`;
    if (attachment.mimeType.startsWith("image/")) {
      return `<a class="chat-attachment-thumbnail" href="${escapeHtml(attachment.dataUrl)}" target="_blank" rel="noreferrer" title="${escapeHtml(label)}"><img src="${escapeHtml(attachment.dataUrl)}" alt="${escapeHtml(attachment.name)}"></a>`;
    }
    return `<span class="chat-attachment-file" title="${escapeHtml(label)}">${escapeHtml(attachment.name)}</span>`;
  }).join("")}</div>`;
}

export async function addChatAttachments(files: FileList | File[]): Promise<void> {
  if (state.chatStreaming) return;
  const incoming = Array.from(files);
  if (!incoming.length) return;
  if (state.chatPendingAttachments.length + incoming.length > MAX_CHAT_ATTACHMENT_COUNT) {
    toast(`一次最多添加 ${MAX_CHAT_ATTACHMENT_COUNT} 个附件。`, "error");
    return;
  }

  let totalBytes = state.chatPendingAttachments.reduce((total, attachment) => total + attachment.size, 0);
  const additions: ChatAttachment[] = [];
  for (const file of incoming) {
    const mimeType = resolveAttachmentMimeType(file);
    if (!SUPPORTED_ATTACHMENT_TYPES.has(mimeType)) {
      toast(`不支持 ${file.name || "该文件"} 的格式。`, "error");
      continue;
    }
    if (file.size === 0 || file.size > MAX_CHAT_ATTACHMENT_BYTES) {
      toast(`${file.name || "附件"} 不能超过 4 MiB。`, "error");
      continue;
    }
    totalBytes += file.size;
    if (totalBytes > MAX_CHAT_ATTACHMENT_TOTAL_BYTES) {
      toast("附件总大小不能超过 8 MiB。", "error");
      break;
    }
    additions.push({
      name: file.name || `attachment-${state.chatPendingAttachments.length + additions.length + 1}`,
      mimeType,
      size: file.size,
      dataUrl: await readFileAsDataUrl(file, mimeType)
    });
  }
  if (!additions.length) return;
  state.chatPendingAttachments = [...state.chatPendingAttachments, ...additions];
  renderChatPendingAttachments();
}

export function removeChatAttachment(index: number): void {
  state.chatPendingAttachments = state.chatPendingAttachments.filter((_attachment, candidateIndex) => candidateIndex !== index);
  renderChatPendingAttachments();
}

export function renderChatPendingAttachments(): void {
  const container = document.querySelector<HTMLElement>("#chatPendingAttachments");
  if (!container) return;
  container.innerHTML = state.chatPendingAttachments.map((attachment, index) => `
    <div class="chat-pending-attachment">
      ${attachment.mimeType.startsWith("image/") ? `<img src="${escapeHtml(attachment.dataUrl)}" alt="">` : "<span class=\"chat-pending-file-icon\">FILE</span>"}
      <span title="${escapeHtml(attachment.name)}">${escapeHtml(attachment.name)}</span>
      <button type="button" data-action="chat-remove-attachment" data-id="${index}" title="移除附件" aria-label="移除 ${escapeHtml(attachment.name)}">x</button>
    </div>
  `).join("");
}

function resolveAttachmentMimeType(file: File): string {
  const browserType = String(file.type || "").toLowerCase();
  if (browserType) return browserType;
  const extension = file.name.toLowerCase().split(".").pop() || "";
  return ({ txt: "text/plain", md: "text/markdown", csv: "text/csv", json: "application/json", html: "text/html", xml: "application/xml" } as Record<string, string>)[extension] || "";
}

function readFileAsDataUrl(file: File, mimeType: string): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onerror = () => reject(new Error(`无法读取 ${file.name || "附件"}`));
    reader.onload = () => resolve(String(reader.result).replace(/^data:[^;,]+/, `data:${mimeType}`));
    reader.readAsDataURL(file);
  });
}

function formatAttachmentSize(size: number): string {
  return size >= 1024 * 1024 ? `${(size / (1024 * 1024)).toFixed(1)} MiB` : `${Math.max(1, Math.ceil(size / 1024))} KiB`;
}

/** 仅更新流式输出文本(避免整个消息区重渲染) */
function renderChatStreamingText(): void {
  const streamingEl = document.querySelector<HTMLElement>(".chat-message-streaming .chat-stream-text");
  if (streamingEl) {
    streamingEl.innerHTML = `${renderMarkdown(state.chatStreamingText || "")}<span class="chat-cursor">▋</span>`;
  }
}

function renderChatStreamingReasoning(): void {
  const streamingEl = document.querySelector<HTMLElement>(".chat-message-streaming .chat-stream-reasoning");
  if (streamingEl) {
    streamingEl.outerHTML = renderStreamingReasoning(state.chatStreamingReasoning);
    return;
  }
  const textEl = document.querySelector<HTMLElement>(".chat-message-streaming .chat-stream-text");
  textEl?.insertAdjacentHTML("beforebegin", renderStreamingReasoning(state.chatStreamingReasoning));
}

function renderChatStreamingStatus(): void {
  const statusEl = document.querySelector<HTMLElement>(".chat-message-streaming .chat-stream-status");
  if (statusEl) {
    statusEl.innerHTML = `${escapeHtml(state.chatStreamingStatus || "连接模型中")}<span></span>`;
  }
}

function renderChatStreamingSteps(): void {
  const stepsEl = document.querySelector<HTMLElement>(".chat-message-streaming .chat-stream-steps");
  if (stepsEl) {
    stepsEl.innerHTML = renderProcessSteps();
  }
}

function renderChatStreamingSources(): void {
  const sourcesEl = document.querySelector<HTMLElement>(".chat-message-streaming .chat-stream-sources");
  if (sourcesEl) {
    sourcesEl.innerHTML = renderSources(state.chatStreamingSources);
  }
}

/** 渲染输入框区域(静态结构,事件在 main.ts 绑定) */
function renderChatInput(): void {
  // 输入框是静态 HTML,这里只做占位检查
  const inputEl = document.querySelector<HTMLTextAreaElement>("#chatInput");
  if (inputEl) {
    inputEl.disabled = state.chatStreaming;
  }
  renderChatPendingAttachments();
  document.querySelectorAll<HTMLInputElement>("[data-chat-capability]").forEach((input) => {
    input.disabled = state.chatStreaming;
  });
  const modelSelect = document.querySelector<HTMLSelectElement>("#chatModelSelect");
  if (modelSelect) {
    modelSelect.innerHTML = state.chatAvailableModels
      .map((model) => `
        <option value="${escapeHtml(model.modelName)}" ${model.modelName === state.chatSelectedModel ? "selected" : ""}>
          ${escapeHtml(model.label)}
        </option>
      `)
      .join("");
    modelSelect.disabled = state.chatStreaming || state.chatAvailableModels.length <= 1;
  }
}

/** Scroll only when the reader is already following the live tail. */
function shouldFollowChatTail(): boolean {
  const messagesEl = document.querySelector<HTMLElement>("#chatMessages");
  return Boolean(messagesEl && isNearScrollBottom(messagesEl));
}

function updateChatScrollToLatestButton(): void {
  const messagesEl = document.querySelector<HTMLElement>("#chatMessages");
  const latestButton = document.querySelector<HTMLButtonElement>("#chatScrollToLatest");
  if (!latestButton) return;
  latestButton.hidden = !state.chatStreaming || !messagesEl || isNearScrollBottom(messagesEl);
}

function cacheCurrentChatMessages(): void {
  if (state.chatCurrentSessionId) {
    chatMessageCache.set(state.chatCurrentSessionId, state.chatMessages);
  }
}

/** Explicit user actions and initial sends may always move to the latest reply. */
function scrollChatToBottom(): void {
  const messagesEl = document.querySelector<HTMLElement>("#chatMessages");
  if (messagesEl) {
    messagesEl.scrollTop = messagesEl.scrollHeight;
  }
}

export function applyChatSuggestion(prompt: string): void {
  const inputEl = document.querySelector<HTMLTextAreaElement>("#chatInput");
  if (!inputEl || state.chatStreaming) return;
  inputEl.value = prompt;
  inputEl.focus();
}

// 让 router.ts 的 render() 能调用本模块的 renderChatPage
// (已在 export function renderChatPage 中定义)
